import { apiUrl } from "./api";
import { previewKind, type PreviewKind } from "./fileTypes";
import { watermarkImageBlob } from "./burnWatermark";

export type FetchedDocument = {
  buf: ArrayBuffer;
  contentType: string;
  filename: string;
  previewKind: PreviewKind;
  servingPdf?: boolean;
};

const ERROR_LABELS: Record<string, string> = {
  session_expired: "Your session expired. Refresh and sign in again.",
  email_required: "Complete email verification first.",
  password_required: "Enter the link password to view documents.",
  nda_required: "Sign the NDA to view documents.",
  not_found: "This file is no longer available.",
  not_ready: "This file is still processing. Try again in a moment.",
  missing_object: "File storage is missing. Contact the deal team.",
  load_timeout: "This file is taking too long to load. Check your connection, then retry or download it.",
  download_forbidden: "Downloads are disabled for this link.",
  encryption_not_configured: "Document encryption is not configured on the server.",
  stage_forbidden: "You do not have access to this document tier.",
  fingerprint_mismatch: "This session was opened on another device. Verify your email again.",
  rate_limited: "Too many attempts. Wait a minute and try again.",
};

export function formatDocumentError(code: string): string {
  if (ERROR_LABELS[code]) return ERROR_LABELS[code];
  if (code.startsWith("content_")) return `Could not load document (${code.replace("content_", "HTTP ")})`;
  if (code.startsWith("download_")) return `Download failed (${code.replace("download_", "HTTP ")})`;
  return code;
}

type FetchOpts = {
  sessionId?: string;
  admin?: boolean;
  signal?: AbortSignal;
  /** When set, image downloads are stamped client-side before save. */
  watermarkText?: string;
};

export async function fetchDocumentBytes(url: string, opts: FetchOpts = {}): Promise<FetchedDocument> {
  const headers: Record<string, string> = {};
  if (opts.sessionId) headers["X-Viewer-Session"] = opts.sessionId;
  if (opts.admin) {
    const token = localStorage.getItem("adminSessionToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const fp = localStorage.getItem("deviceFp");
  if (fp) headers["X-Device-Fingerprint"] = fp;

  const res = await fetch(apiUrl(url), { headers, signal: opts.signal });
  const contentType = res.headers.get("content-type") || "";

  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const err = (await res.json().catch(() => ({}))) as { error?: string; status?: string };
      throw new Error(err.error || `request_failed_${res.status}`);
    }
    throw new Error(`content_${res.status}`);
  }

  if (contentType.includes("application/json")) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || "unexpected_json_response");
  }

  const buf = await res.arrayBuffer();
  const headerName = res.headers.get("x-filename") || undefined;
  const headerKind = res.headers.get("x-preview-kind") as PreviewKind | null;
  const filename = headerName || "document";
  const resolved = headerKind || previewKind(filename, contentType);
  const servingPdf = res.headers.get("x-serving-pdf") === "1";

  return { buf, contentType, filename, previewKind: resolved, servingPdf };
}

export async function downloadDocument(url: string, fallbackName: string, opts: FetchOpts = {}): Promise<void> {
  const headers: Record<string, string> = {};
  if (opts.sessionId) headers["X-Viewer-Session"] = opts.sessionId;
  if (opts.admin) {
    const token = localStorage.getItem("adminSessionToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const fp = localStorage.getItem("deviceFp");
  if (fp) headers["X-Device-Fingerprint"] = fp;

  const res = await fetch(apiUrl(url), { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `download_${res.status}` }));
    throw new Error((err as { error?: string }).error || `download_${res.status}`);
  }
  let blob = await res.blob();
  if (opts.watermarkText && blob.type.startsWith("image/")) {
    blob = await watermarkImageBlob(blob, opts.watermarkText);
  }
  const name =
    res.headers.get("content-disposition")?.match(/filename="?([^";]+)"?/)?.[1] || fallbackName || "download";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}
